'use client';
import {
  type Row,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  FilterFn,
  getFilteredRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedMinMaxValues,
  getFacetedUniqueValues
} from '@tanstack/react-table';
import React, { Dispatch, SetStateAction } from 'react';
import Pagnation from './Pagination';
import { rankItem } from '@tanstack/match-sorter-utils';
import Image from 'next/image';
import cx from 'classnames';
import { PUBLIC } from '@/constants/image/path';

interface ReactTableProperty {
  className?: string;
  headerClassName?: string;
  data: unknown[];
  columns: any;
  onClick?: (row: any) => void;
  enablePagination?: boolean;
  Skeleton?: React.ReactNode;
  search?: string;
  setSearch?: Dispatch<SetStateAction<string>>;
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value);

  // Store the itemRank info
  addMeta({
    itemRank
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

enum Sort {
  ASC = 'asc',
  DESC = 'desc'
}

const ReactTable: React.FC<ReactTableProperty> = ({
  className,
  headerClassName,
  data = [],
  columns,
  onClick,
  enablePagination = false,
  search,
  Skeleton
}) => {
  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter: search
    },
    globalFilterFn: fuzzyFilter,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 8
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    sortingFns: {
      sortingStringNumberFn: (rowA, rowB, columnId) => {
        const percentA = parseFloat(rowA.getValue(columnId));
        const percentB = parseFloat(rowB.getValue(columnId));
        return percentA > percentB ? 1 : percentA < percentB ? -1 : 0;
      }
    }
  });

  if (!data || data?.length === 0) return Skeleton || null;

  const headers = table?.getHeaderGroups()?.[0]?.headers || [];
  const rows = table?.getRowModel?.()?.rows || [];
  const totalPage = table?.getPageCount();

  const onClickRow = (row: Row<any>) => () => {
    onClick?.(row?.original);
  };

  return (
    <>
      <table className={cx('w-full', className)}>
        <thead className='text-xs text-[#848d97]'>
          <tr>
            {headers.map(header => {
              const canSort = header.column.getCanSort();
              const sort: boolean | string = header.column.getIsSorted();

              return (
                <th
                  key={header.id}
                  className={cx('h-9 border-b border-[#e2e8f1] font-normal', headerClassName)}
                  style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                >
                  <div
                    className={cx('flex w-full items-center', {
                      'text-[#515c69]': sort === Sort.ASC || sort === Sort.DESC
                    })}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{
                      [Sort.ASC]: (
                        <Image
                          className='ml-1'
                          src={`${PUBLIC}/icons/arrows/sort-up.svg`}
                          width={6}
                          height={12}
                          alt='arrow-up'
                        />
                      ),
                      [Sort.DESC]: (
                        <Image
                          className='ml-1'
                          src={`${PUBLIC}/icons/arrows/sort-down.svg`}
                          width={6}
                          height={12}
                          alt='arrow-down'
                        />
                      )
                    }[sort as Sort] ??
                      (canSort ? (
                        <Image
                          className='ml-1'
                          src={`${PUBLIC}/icons/arrows/sort.svg`}
                          width={6}
                          height={12}
                          alt='arrow'
                        />
                      ) : null)}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className='text-sm'>
          {rows?.map(row => {
            return (
              <tr
                key={row.id}
                className='cursor-pointer border-b border-[#e2e8f1] hover:bg-[#f6f8fc]'
                onClick={onClickRow(row)}
              >
                {row?.getVisibleCells?.()?.map(cell => (
                  <td className={cx('h-[46px] text-center', className)} key={cell.id}>
                    {flexRender(cell?.column?.columnDef?.cell, cell?.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {enablePagination && totalPage > 1 && <Pagnation table={table} />}
    </>
  );
};

export default ReactTable;
